import {
	Box,
	LinearProgress,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import type { FC } from "react";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import { useGoalDetailHero } from "web_myplann/Goals/Hooks/useGoalDetailHero";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

type Props = {
	goal: GoalDetail;
};

export const GoalDetailHero: FC<Props> = ({ goal }) => {
	const theme = useTheme();
	const { t } = useResource();
	const formatAmount = useFormatGoalAmount();
	const {
		categoryConfig,
		CategoryIcon,
		statusStyle,
		StatusIcon,
		statusLabel,
		coveredAmount,
		progressPct,
	} = useGoalDetailHero(goal);

	return (
		<Box bgcolor={theme.palette.background.paper} px={1.6} pt={2} pb={1.6}>
			<Stack direction="row" alignItems="center" gap={1.2} mb={1.6}>
				<Box
					sx={{
						width: 48,
						height: 48,
						borderRadius: "12px",
						bgcolor: categoryConfig.bgColor,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<CategoryIcon size={24} color={categoryConfig.color} />
				</Box>
				<Stack>
					<Typography variant="h3">{goal.name}</Typography>
					<Typography variant="body2">
						{goal.completed
							? t(Resources.Goals.Detail.completedInYear, {
									year: goal.dueYear,
								})
							: t(Resources.Goals.Detail.dueInYear, {
									dueIn: goal.dueIn,
									year: goal.dueYear,
								})}
					</Typography>
				</Stack>
			</Stack>

			<LinearProgress
				variant="determinate"
				slim
				value={progressPct}
				sx={{
					mb: 0.8,
					"& .MuiLinearProgress-bar": {
						bgcolor: statusStyle.progressColor,
					},
				}}
			/>
			<Stack direction="row" justifyContent="space-between" alignItems="center">
				<Typography variant="bodyStrong" tabular>
					{formatAmount(coveredAmount, goal.currencyCode)} /{" "}
					{formatAmount(goal.savingsTarget, goal.currencyCode)}
				</Typography>
				{StatusIcon && (
					<Stack
						direction="row"
						alignItems="center"
						gap={0.4}
						color={statusStyle.color}
					>
						<StatusIcon size={14} />
						<Typography variant="button">{statusLabel}</Typography>
					</Stack>
				)}
			</Stack>
		</Box>
	);
};
